import authService from "../services/authService";
import { decodeJwtPayload } from "./jwt";

export type AuthSessionValidation = {
  valid: boolean;
  user: any | null;
};

export const isJwtTokenValid = (user: any | null): boolean => {
  if (!user?.access_token || typeof user.access_token !== "string") {
    return false;
  }

  const now = Math.floor(Date.now() / 1000);
  const expiresAt = typeof user.expires_at === "number" ? user.expires_at : undefined;

  // expires_at is computed on this device when the token arrives. A fast
  // mobile clock makes the server exp claim look elapsed and used to reject
  // every fresh token, which bounced the session back to login.
  if (expiresAt !== undefined && expiresAt > now) {
    return true;
  }

  if (expiresAt !== undefined && expiresAt <= now) {
    return false;
  }

  if (user.expired === true) {
    return false;
  }

  const payload = decodeJwtPayload(user.access_token);
  if (payload?.exp !== undefined) {
    const exp = Number(payload.exp);
    if (!Number.isNaN(exp) && exp <= now) {
      return false;
    }
  }

  return true;
};

export const validateAuthSession = async (
  existingUser?: any | null,
  options?: { renew?: boolean },
): Promise<AuthSessionValidation> => {
  let user = existingUser ?? (await authService.getUser());

  if (!user) {
    return { valid: false, user: null };
  }

  if (!isJwtTokenValid(user)) {
    if (options?.renew === false) {
      return { valid: false, user: null };
    }

    try {
      user = (await authService.signinSilent()) ?? null;
    } catch {
      user = null;
    }
  }

  if (user && isJwtTokenValid(user)) {
    return { valid: true, user };
  }

  try {
    await authService.removeUser();
  } catch {
    // Best-effort cleanup for invalid or expired sessions.
  }

  return { valid: false, user: null };
};
